import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  Check,
  ChevronRight,
  CreditCard,
  Loader2,
  Lock,
  MapPin,
  Minus,
  Package,
  Pause,
  Play,
  Plus,
  RefreshCw,
  Search,
  Tag,
  Truck,
  X as XIcon,
} from "lucide-react";
import { detectShopifyCustomerId, detectShopDomain, portalFetch, otpStart, otpVerify, hasOtpSession, REVERIFY, getImpersonation, startImpersonation, exitImpersonation } from "@/lib/portal-session";
import { buildCatalog, cleanTitle, type CatProduct } from "@/lib/subscription-catalog";

// ─────────────────────────────────────────────────────────────────────────────
// ManageSubscriptionsView — full self-service subscription management inside
// the widget. One view, two engines:
//   • FlowIQ (our own engine — Phyto et al): the customer-portal edge fn's
//     native routes (the exact backend the storefront portal + AI agent use).
//   • Loop (loopwork.co — Barkyn): the same fn's /loop/* proxy, contact-scoped
//     server-side, ownership asserted per call by loop-subscriptions.
// Identity = the storefront's logged-in Shopify customer (see portal-session).
// Design language mirrors OrdersView: gray-50 canvas, floating chrome pills,
// white rounded-2xl cards, tiny uppercase section labels.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  organizationId: string;
  flowiqEnabled: boolean;
  loopEnabled: boolean;
  /** Org flag widget_manage_no_login: verify with a one-time code instead of
   *  requiring the store login (server-enforced too). */
  manageNoLogin?: boolean;
  /** The widget registration (name/phone/email) — pre-fills the verify step. */
  userInfo?: { fullName?: string; email?: string; phone?: string } | null;
  /** Cart-line subscription orgs (feature_flags.subscription_checkout.every_weeks,
   *  e.g. Lean Living [2,4,6]): the how-often choices in weeks, and "add a product"
   *  takes any product (these stores have no "Deliver every" variants). */
  subscriptionWeeks?: number[] | null;
  onBack: () => void;
  /** Open the chat view — the escape hatch for anything unusual. */
  onOpenChat?: () => void;
}

type Engine = "flowiq" | "loop";

interface MLine {
  key: string;
  lineId: number | null; // loop
  variantId: string | null; // flowiq
  title: string;
  variantTitle: string | null;
  qty: number;
  price: number | null;
  image: string | null;
  oneTime: boolean;
}

interface MSub {
  engine: Engine;
  id: string; // flowiq uuid | loop numeric as string
  status: "active" | "paused" | "cancelled" | "expired";
  title: string;
  freqLabel: string;
  currency: string;
  total: number | null;
  /** Discount-ladder subs: the price before the subscriber discount, and its percent. */
  fullTotal?: number | null;
  discountPercent?: number | null;
  nextDate: string | null;
  lines: MLine[];
  raw: any;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  ZAR: "R", USD: "$", EUR: "€", GBP: "£", AUD: "A$", CAD: "C$", NZD: "NZ$",
};
const money = (n: number | string | null | undefined, cur: string): string => {
  const v = typeof n === "string" ? parseFloat(n) : n;
  if (v == null || Number.isNaN(v)) return "";
  return `${CURRENCY_SYMBOLS[cur] || `${cur} `}${v.toFixed(2)}`;
};
const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return String(iso); }
};
const daysUntil = (iso: string | null): string => {
  if (!iso) return "";
  const d = Math.round((new Date(iso).getTime() - Date.now()) / 86400000);
  if (d === 0) return "Today";
  if (d < 0) return `${Math.abs(d)}d overdue`;
  return `in ${d} day${d === 1 ? "" : "s"}`;
};
const toDateInput = (d: Date): string => {
  const mm = `0${d.getMonth() + 1}`.slice(-2);
  const dd = `0${d.getDate()}`.slice(-2);
  return `${d.getFullYear()}-${mm}-${dd}`;
};
const pctLabel = (n: number): string => `${Number.isInteger(n) ? n : n.toFixed(1).replace(/\.0$/, "")}%`;

// ── Normalizers ──────────────────────────────────────────────────────────────

function normFlowiq(sub: any, variantInfo: Record<string, any>): MSub {
  const d = sub.data || {};
  const status: MSub["status"] = d.cancelled === true ? "cancelled" : d.paused === true ? "paused" : "active";
  const orders: any[] = Array.isArray(sub.orders) ? sub.orders : [];
  const next = orders
    .filter((o) => o.status === "request" || o.status === "queued")
    .sort((a, b) => String(a.scheduled_for).localeCompare(String(b.scheduled_for)))[0] || null;
  const iv = Number(d.interval_value), iu = String(d.interval_unit || "");
  const freqLabel = iv && iu
    ? (iv === 1 && iu === "months" ? "Monthly" : iv === 1 && iu === "weeks" ? "Weekly" : `Every ${iv} ${iu}`)
    : (typeof d.place === "string" && d.place ? d.place : "Recurring");
  const lines: MLine[] = (d.variants || []).map((v: any, i: number) => {
    const info = variantInfo[String(v.variant_id)] || {};
    return {
      key: `fi-${v.variant_id}-${i}`,
      lineId: null,
      variantId: String(v.variant_id),
      title: cleanTitle(info.product_title) || `Item ${i + 1}`,
      variantTitle: null,
      qty: Number(v.quantity) || 1,
      price: info.price != null ? Number(info.price) : null,
      image: info.image_src || null,
      oneTime: false,
    };
  });
  const title = lines.map((l) => l.title).filter(Boolean).slice(0, 3).join(", ") || "Your subscription";
  return {
    engine: "flowiq",
    id: String(sub.id),
    status,
    title,
    freqLabel,
    currency: "ZAR",
    // A discount-ladder sub (Lean Living) is charged its subscriber price
    // (customer-portal's subscriber_price, the engine's own rule).
    total: sub.subscriber_price ? Number(sub.subscriber_price.amount) : d.recurring_amount != null ? Number(d.recurring_amount) : null,
    fullTotal: sub.subscriber_price ? Number(sub.subscriber_price.before_discount) : null,
    discountPercent: sub.subscriber_price ? Number(sub.subscriber_price.percent) : null,
    nextDate: next?.scheduled_for ?? null,
    lines,
    raw: sub,
  };
}

function normLoop(sub: any): MSub {
  const st = String(sub.status || "").toUpperCase();
  const status: MSub["status"] =
    st === "ACTIVE" ? "active" : st === "PAUSED" ? "paused" : st === "CANCELLED" ? "cancelled" : "expired";
  const lines: MLine[] = (sub.lines || []).map((l: any, i: number) => ({
    key: `lp-${l.line_id ?? i}`,
    lineId: l.line_id ?? null,
    variantId: l.variant_shopify_id ? String(l.variant_shopify_id) : null,
    title: cleanTitle(l.title) || `Item ${i + 1}`,
    variantTitle: l.variant_title && l.variant_title !== "Default Title" ? l.variant_title : null,
    qty: Number(l.quantity) || 1,
    price: l.price != null ? Number(l.price) : null,
    image: l.image_url || null,
    oneTime: !!l.is_one_time,
  }));
  const title = lines.filter((l) => !l.oneTime).map((l) => l.title).slice(0, 3).join(", ") || "Your subscription";
  const freq = String(sub.frequency || "recurring");
  return {
    engine: "loop",
    id: String(sub.subscription_id),
    status,
    title,
    freqLabel: freq.charAt(0).toUpperCase() + freq.slice(1),
    currency: sub.currency || "EUR",
    total: sub.items_total != null ? Number(sub.items_total) : null,
    nextDate: sub.next_billing_date ?? null,
    lines,
    raw: sub,
  };
}

// ── Shared atoms ─────────────────────────────────────────────────────────────

const StatusPill: React.FC<{ status: MSub["status"] }> = ({ status }) => {
  const map: Record<MSub["status"], string> = {
    active: "bg-green-50 text-green-700",
    paused: "bg-amber-50 text-amber-700",
    cancelled: "bg-red-50 text-red-700",
    expired: "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${map[status]} text-[10px] font-medium capitalize`}>
      {status}
    </span>
  );
};

const Section: React.FC<{ label: string; children: React.ReactNode; right?: React.ReactNode }> = ({ label, children, right }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-4 pt-3 pb-2 flex items-center justify-between">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</span>
      {right}
    </div>
    <div className="px-4 pb-3">{children}</div>
  </div>
);

const ActionBtn: React.FC<{
  onClick: () => void; busy?: boolean; variant?: "solid" | "outline" | "danger" | "ghost";
  disabled?: boolean; children: React.ReactNode; className?: string;
}> = ({ onClick, busy, variant = "outline", disabled, children, className }) => {
  const base = "inline-flex items-center justify-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const styles: Record<string, string> = {
    solid: "bg-gray-900 text-white hover:bg-black",
    outline: "bg-white text-gray-800 border border-gray-300 hover:bg-gray-50",
    danger: "bg-white text-red-600 border border-red-200 hover:bg-red-50",
    ghost: "text-gray-500 hover:text-gray-800 underline underline-offset-2",
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled || busy} className={`${base} ${styles[variant]} ${className || ""}`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : null}
      {children}
    </button>
  );
};

const Modal: React.FC<{ open: boolean; onClose: () => void; title: string; children: React.ReactNode }> = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="absolute inset-0 z-40 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-gray-900/40" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl shadow-xl p-5 max-h-[85%] overflow-y-auto overscroll-contain">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
            <XIcon size={14} className="text-gray-600" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (p) => (
  <input {...p} className={`w-full border border-gray-300 rounded-xl px-3 py-2 text-base sm:text-sm bg-white focus:outline-none focus:border-gray-500 ${p.className || ""}`} />
);

// ── Storefront catalogue (same-origin /products.json — works on any Shopify
//    storefront where the widget is embedded) ────────────────────────────────

const catalogCache: Record<"twin" | "cartline", CatProduct[] | null> = { twin: null, cartline: null };
async function loadCatalog(cartLine = false): Promise<CatProduct[]> {
  const mode = cartLine ? "cartline" : "twin";
  if (catalogCache[mode]) return catalogCache[mode]!;
  const res = await fetch("/products.json?limit=250", { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error("Could not load products");
  const d = await res.json();
  catalogCache[mode] = buildCatalog((d?.products as any[]) || [], cartLine);
  return catalogCache[mode]!;
}

// ─────────────────────────────────────────────────────────────────────────────

const ManageSubscriptionsView: React.FC<Props> = ({ organizationId, flowiqEnabled, loopEnabled, manageNoLogin, userInfo, subscriptionWeeks, onBack, onOpenChat }) => {
  const customerId = useMemo(() => detectShopifyCustomerId(), []);
  const shopDomain = useMemo(() => detectShopDomain(), []);
  const storeIdentity = !!(customerId && shopDomain);

  const [phase, setPhase] = useState<"login" | "loading" | "error" | "ready">(
    storeIdentity || hasOtpSession() || getImpersonation() ? "loading" : "login",
  );
  const [error, setError] = useState<string>("");
  const [notice, setNotice] = useState<string>("");
  const [subs, setSubs] = useState<MSub[]>([]);
  const [flowiqPayload, setFlowiqPayload] = useState<any>(null); // orders/variant_info/payment_method
  const [selected, setSelected] = useState<string | null>(null);
  const autoOpened = useRef(false);
  const [busyKey, setBusyKey] = useState<string>("");
  // Admin "view as customer" (server-verified; UI is just the door).
  const [impersonating, setImpersonating] = useState<{ label: string } | null>(getImpersonation());
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [adminIdent, setAdminIdent] = useState("");
  const [adminErr, setAdminErr] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);

  const adminGo = async () => {
    const ident = adminIdent.trim();
    if (!adminCode.trim() || !ident) { setAdminErr("Access code and a customer email/number are required."); return; }
    setAdminBusy(true); setAdminErr("");
    try {
      const isEmail = ident.includes("@");
      const r = await startImpersonation(
        storeIdentity ? shopDomain : null, storeIdentity ? customerId : null,
        adminCode.trim(), isEmail ? { email: ident } : { phone: ident },
      );
      setImpersonating({ label: r.label });
      setAdminOpen(false); setAdminIdent("");
      setSelected(null); autoOpened.current = false;
      setNotice(""); setError("");
      setPhase("loading"); load();
    } catch (e: any) {
      if (e?.message === REVERIFY) { setAdminOpen(false); setPhase("login"); setNotice("Verify yourself first, then open admin view."); }
      else setAdminErr(e?.message || "Could not open that customer's view.");
    } finally {
      setAdminBusy(false);
    }
  };

  const adminExit = () => {
    exitImpersonation();
    setImpersonating(null);
    setSelected(null); autoOpened.current = false;
    setNotice(""); setError("");
    if (storeIdentity || hasOtpSession()) { setPhase("loading"); load(); }
    else setPhase("login");
  };

  const call = (path: string, body?: unknown, method?: string) =>
    portalFetch(storeIdentity ? shopDomain : null, storeIdentity ? customerId : null, path, { body, method });

  const load = async () => {
    setError("");
    try {
      const jobs: Promise<any>[] = [];
      jobs.push(flowiqEnabled ? call("/subscriptions", undefined, "GET") : Promise.resolve(null));
      jobs.push(loopEnabled ? call("/loop/subscriptions") : Promise.resolve(null));
      const [fi, lp] = await Promise.all(jobs);
      const list: MSub[] = [];
      if (fi) {
        setFlowiqPayload(fi);
        for (const s of fi.subscriptions || []) list.push(normFlowiq(s, fi.variant_info || {}));
      }
      if (lp) for (const s of lp.subscriptions || []) list.push(normLoop(s));
      // Live things first, dead things last, newest next-date first within.
      const rank: Record<string, number> = { active: 0, paused: 1, cancelled: 2, expired: 3 };
      list.sort((a, b) => rank[a.status] - rank[b.status] || String(a.nextDate || "9").localeCompare(String(b.nextDate || "9")));
      setSubs(list);
      setPhase("ready");
      const alive = list.filter((s) => s.status !== "cancelled" && s.status !== "expired");
      if (!autoOpened.current && alive.length === 1) {
        autoOpened.current = true;
        setSelected(`${alive[0].engine}:${alive[0].id}`);
      }
    } catch (e: any) {
      if (e?.message === REVERIFY) {
        setError("");
        setNotice("Your verified session ended — verify again to continue.");
        setPhase("login");
        return;
      }
      setError(e?.message || "Could not load your subscriptions.");
      setPhase("error");
    }
  };

  useEffect(() => {
    if (phase === "loading") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => { try { await load(); } catch { /* surfaced in load */ } };

  // One busy-wrapped action runner: every mutation reloads, so the screen is
  // always the server's truth, never an optimistic guess.
  const run = async (key: string, fn: () => Promise<any>, successNotice?: string) => {
    setBusyKey(key); setError(""); setNotice("");
    try {
      const r = await fn();
      await refresh();
      setNotice(successNotice || r?.message || "Done.");
      return true;
    } catch (e: any) {
      if (e?.message === REVERIFY) {
        setNotice("Your verified session ended — verify again to continue.");
        setPhase("login");
        return false;
      }
      setError(e?.message || "That didn't work — please try again.");
      return false;
    } finally {
      setBusyKey("");
    }
  };

  const current = subs.find((s) => `${s.engine}:${s.id}` === selected) || null;

  // ── chrome ──
  const header = (
    <motion.div
      className="absolute top-3 left-3 right-3 z-20 flex items-center pointer-events-none"
      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
    >
      <motion.button
        onClick={() => (current ? setSelected(null) : onBack())}
        className="pointer-events-auto h-9 w-9 rounded-full bg-white shadow-md ring-1 ring-black/5 flex items-center justify-center hover:bg-gray-50 transition-colors"
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} title="Back" aria-label="Back"
      >
        <ArrowLeft size={18} className="text-gray-700" />
      </motion.button>
      <div className="pointer-events-auto ml-auto mr-11 flex items-center gap-1.5 h-9 px-3 rounded-full bg-white shadow-md ring-1 ring-black/5">
        <RefreshCw size={14} className="text-gray-600" />
        <span className="text-sm font-semibold text-gray-800">Manage Subscription</span>
      </div>
    </motion.div>
  );

  return (
    <motion.div
      className="relative h-full flex flex-col bg-gray-50"
      style={{ fontSize: "16px" }}
    >
      {header}
      {/* Admin "view as customer" modal — server verifies allowlist + code. */}
      <Modal open={adminOpen} onClose={() => { setAdminOpen(false); setAdminErr(""); }} title="Staff — view as customer">
        <p className="text-xs text-gray-500 mb-3">Opens a customer's subscription manager exactly as they'd see it. Requires the staff access code.</p>
        <div className="space-y-2">
          <Input type="password" placeholder="Access code" autoComplete="off" value={adminCode} onChange={(e) => setAdminCode(e.target.value)} />
          <Input placeholder="Customer email or WhatsApp number" autoComplete="off" value={adminIdent}
            onChange={(e) => setAdminIdent(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") adminGo(); }} />
          <ActionBtn variant="solid" className="w-full" busy={adminBusy} onClick={adminGo}>View as customer</ActionBtn>
          {adminErr && <p className="text-[11px] text-red-600">{adminErr}</p>}
        </div>
      </Modal>

      <div className="flex-1 overflow-y-auto overscroll-contain pt-16">
        {phase === "login" && (
          <VerifyGate
            organizationId={organizationId}
            shopDomain={shopDomain}
            manageNoLogin={manageNoLogin === true}
            userInfo={userInfo}
            notice={notice}
            onVerified={() => { setNotice(""); setPhase("loading"); load(); }}
            onStaffAccess={manageNoLogin ? () => setAdminOpen(true) : undefined}
          />
        )}
        {phase === "loading" && (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-gray-400">
            <Loader2 size={24} className="animate-spin" />
            <span className="text-xs">Loading your subscription…</span>
          </div>
        )}
        {phase === "error" && (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center"><XIcon size={20} className="text-red-500" /></div>
            <p className="text-sm font-medium text-gray-800">Couldn't load your subscription</p>
            <p className="text-xs text-gray-500">{error}</p>
            <ActionBtn onClick={() => { setPhase("loading"); load(); }} variant="outline"><RefreshCw size={12} /> Try again</ActionBtn>
          </div>
        )}
        {phase === "ready" && (
          <div className="p-3 space-y-3 pb-6">
            {impersonating && (
              <div className="bg-gray-900 text-white rounded-2xl px-4 py-2.5 flex items-center justify-between gap-2">
                <span className="text-[11px] min-w-0 truncate">Viewing as <strong>{impersonating.label}</strong></span>
                <span className="flex gap-1.5 flex-shrink-0">
                  <button type="button" onClick={() => { setAdminOpen(true); }} className="text-[10px] font-semibold bg-white/15 hover:bg-white/25 rounded-lg px-2.5 py-1.5">Switch</button>
                  <button type="button" onClick={adminExit} className="text-[10px] font-semibold bg-white text-gray-900 hover:bg-gray-100 rounded-lg px-2.5 py-1.5">Exit</button>
                </span>
              </div>
            )}
            {(error || notice) && (
              <div className={`rounded-xl px-3.5 py-2.5 text-xs ${error ? "bg-red-50 text-red-700 border border-red-100" : "bg-green-50 text-green-700 border border-green-100"}`}>
                {error || notice}
              </div>
            )}
            {!current && (
              subs.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center"><RefreshCw size={24} className="text-gray-400" /></div>
                  <p className="text-sm font-medium text-gray-800">No subscriptions on this account</p>
                  <p className="text-xs text-gray-500">When you start a subscription it'll be manageable right here.</p>
                </div>
              ) : (
                <>
                  <div className="px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    {subs.length === 1 ? "Your subscription" : `Your subscriptions (${subs.length})`}
                  </div>
                  {subs.map((s) => (
                    <button
                      key={`${s.engine}:${s.id}`} type="button"
                      onClick={() => setSelected(`${s.engine}:${s.id}`)}
                      className="w-full text-left bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-sm font-semibold text-gray-900 truncate">{s.title}</span>
                            <StatusPill status={s.status} />
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {s.freqLabel}
                            {s.total != null && <> · {money(s.total, s.currency)} {s.discountPercent ? "next delivery" : "per delivery"}</>}
                            {!!s.discountPercent && <> ({pctLabel(s.discountPercent)} subscriber discount)</>}
                            {s.status === "active" && s.nextDate && <> · next {fmtDate(s.nextDate)}</>}
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </>
              )
            )}
            {current && current.engine === "flowiq" && (
              <FlowiqDetail sub={current} payload={flowiqPayload} call={call} run={run} busyKey={busyKey} onOpenChat={onOpenChat} subscriptionWeeks={subscriptionWeeks} />
            )}
            {current && current.engine === "loop" && (
              <LoopDetail sub={current} call={call} run={run} busyKey={busyKey} onOpenChat={onOpenChat} />
            )}
            {manageNoLogin && !impersonating && (
              <p className="text-center pt-1">
                <button type="button" onClick={() => setAdminOpen(true)} className="text-[9px] text-gray-300 hover:text-gray-500">Staff access</button>
              </p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ── Verify gate ──────────────────────────────────────────────────────────────
// Two doors, depending on the org's widget_manage_no_login flag:
//   ON  → one-time code to the matched contact's OWN WhatsApp/email (channel
//         possession = proof), store login offered underneath.
//   OFF → store login only (the storefront portal's bar).

const VerifyGate: React.FC<{
  organizationId: string;
  shopDomain: string | null;
  manageNoLogin: boolean;
  userInfo?: { fullName?: string; email?: string; phone?: string } | null;
  notice?: string;
  onVerified: () => void;
  onStaffAccess?: () => void;
}> = ({ organizationId, shopDomain, manageNoLogin, userInfo, notice, onVerified, onStaffAccess }) => {
  const [ident, setIdent] = useState<string>(userInfo?.phone || userInfo?.email || "");
  const [step, setStep] = useState<"start" | "code">("start");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>(notice || "");
  const [err, setErr] = useState<string>("");
  const [ref, setRef] = useState<string>("");
  const [sentTo, setSentTo] = useState<string>("");
  const [channel, setChannel] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    const v = ident.trim();
    if (!v) { setErr("Enter the phone number or email you use with us."); return; }
    setBusy(true); setErr(""); setMsg("");
    try {
      const isEmail = v.includes("@");
      const r = await otpStart(organizationId, isEmail ? { email: v } : { phone: v });
      if (!r.otp_ref) {
        // Anti-enumeration shape: sent=true with no ref = no matching account.
        setErr("We couldn't find a subscription account for that. Check the details, or log in to your store account below.");
        return;
      }
      setRef(r.otp_ref); setSentTo(r.sent_to || ""); setChannel(r.channel || "");
      setStep("code"); setCode(""); setCooldown(45);
    } catch (e: any) {
      setErr(e?.message || "Could not send a code — try again.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (code.replace(/\D/g, "").length < 4) { setErr("Enter the 6-digit code."); return; }
    setBusy(true); setErr("");
    try {
      const r = await otpVerify(ref, code);
      if (r.verified) { onVerified(); return; }
      setErr(r.reason || "That code isn't right.");
      if (r.reason && /new code/i.test(r.reason)) setStep("start");
    } catch (e: any) {
      setErr(e?.message || "Verification failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full flex flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="w-14 h-14 rounded-full bg-gray-900 flex items-center justify-center"><Lock size={22} className="text-white" /></div>
      <div>
        <p className="text-sm font-semibold text-gray-900 mb-1">
          {manageNoLogin ? "Let's make sure it's you" : "Sign in to manage your subscription"}
        </p>
        <p className="text-xs text-gray-500 leading-relaxed max-w-[280px]">
          {manageNoLogin
            ? "We'll send a one-time code to the WhatsApp or email on your account — managing a subscription changes real orders, so we double-check."
            : "Log in to your store account and we'll open your subscription right here — pause, move deliveries, change products, update your card, everything."}
        </p>
      </div>
      {msg && <p className="text-[11px] text-amber-600 max-w-[280px]">{msg}</p>}

      {manageNoLogin && step === "start" && (
        <div className="w-full max-w-[280px] space-y-2">
          <Input
            placeholder="Phone number or email"
            value={ident}
            onChange={(e) => setIdent(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          />
          <ActionBtn variant="solid" className="w-full" busy={busy} onClick={send}>Send me a code</ActionBtn>
        </div>
      )}
      {manageNoLogin && step === "code" && (
        <div className="w-full max-w-[280px] space-y-2">
          <p className="text-[11px] text-gray-500">
            Code sent {channel === "email" ? "by email" : "on WhatsApp"}{sentTo ? ` to ${sentTo}` : ""}.
          </p>
          <Input
            placeholder="6-digit code"
            inputMode="numeric"
            autoFocus
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter") verify(); }}
            className="text-center tracking-[0.4em] font-semibold"
          />
          <ActionBtn variant="solid" className="w-full" busy={busy} onClick={verify}>Verify</ActionBtn>
          <button
            type="button"
            disabled={cooldown > 0 || busy}
            onClick={send}
            className="text-[11px] text-gray-500 hover:text-gray-800 underline underline-offset-2 disabled:opacity-40 disabled:no-underline"
          >
            {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
          </button>
        </div>
      )}
      {err && <p className="text-[11px] text-red-600 max-w-[280px]">{err}</p>}

      <div className={manageNoLogin ? "pt-1 border-t border-gray-200 w-full max-w-[280px] flex flex-col items-center gap-2" : "flex flex-col items-center gap-2"}>
        {manageNoLogin && <p className="text-[10px] text-gray-400 pt-2">or</p>}
        <button
          type="button"
          onClick={() => { window.location.href = "/account/login"; }}
          className={manageNoLogin
            ? "text-[11px] font-semibold text-gray-700 hover:text-black underline underline-offset-2"
            : "bg-gray-900 text-white rounded-xl px-5 py-2.5 text-xs font-semibold hover:bg-black transition-colors"}
        >
          Log in to your store account
        </button>
        {!shopDomain && !manageNoLogin && (
          <p className="text-[10px] text-gray-400">Subscription management is available on the store's website.</p>
        )}
      </div>
      {onStaffAccess && (
        <button type="button" onClick={onStaffAccess} className="text-[9px] text-gray-300 hover:text-gray-500 pt-2">Staff access</button>
      )}
    </div>
  );
};

// ── Line row (shared visual) ─────────────────────────────────────────────────

const LineRow: React.FC<{
  line: MLine; currency: string;
  onQty?: (delta: number) => void; onRemove?: () => void; busy?: boolean;
}> = ({ line, currency, onQty, onRemove, busy }) => (
  <div className={`flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0 ${busy ? "opacity-50" : ""}`}>
    <div className="w-11 h-11 rounded-lg bg-gray-50 border border-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
      {line.image ? <img src={line.image} alt="" className="w-full h-full object-cover" /> : <Package size={15} className="text-gray-300" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-gray-900 leading-tight truncate">{line.title}</p>
      {line.variantTitle && <p className="text-[10px] text-gray-500 truncate">{line.variantTitle}</p>}
      <div className="flex items-center gap-2 mt-1">
        {onQty ? (
          <span className="inline-flex items-center gap-2">
            <button type="button" disabled={busy} onClick={() => onQty(-1)} className="h-6 w-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"><Minus size={11} /></button>
            <span className="text-xs tabular-nums w-4 text-center">{line.qty}</span>
            <button type="button" disabled={busy} onClick={() => onQty(1)} className="h-6 w-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"><Plus size={11} /></button>
          </span>
        ) : (
          <span className="text-[10px] text-gray-500">Qty {line.qty}</span>
        )}
        {line.oneTime && (
          <span className="text-[9px] font-semibold text-blue-600 bg-blue-50 rounded px-1 py-0.5">next order only</span>
        )}
      </div>
    </div>
    <div className="flex flex-col items-end gap-1 flex-shrink-0">
      {line.price != null && <span className="text-xs font-medium text-gray-700 tabular-nums">{money(line.price, currency)}</span>}
      {onRemove && (
        <button type="button" disabled={busy} onClick={onRemove} className="text-[10px] text-gray-400 hover:text-red-600 underline underline-offset-2 disabled:opacity-40">
          Remove
        </button>
      )}
    </div>
  </div>
);

// ── Catalogue picker (shared sheet) ──────────────────────────────────────────

const CataloguePicker: React.FC<{
  open: boolean; onClose: () => void; mode: "sub" | "onetime";
  onPick: (p: CatProduct) => void; busyHandle: string; cartLine?: boolean;
}> = ({ open, onClose, mode, onPick, busyHandle, cartLine = false }) => {
  const [items, setItems] = useState<CatProduct[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open) return;
    setFailed(false);
    loadCatalog(cartLine).then(setItems).catch(() => setFailed(true));
  }, [open, cartLine]);
  if (!open) return null;
  const list = (items || []).filter((p) => {
    if (mode === "sub" ? p.subVariantId == null || p.noSubscription : p.oneVariantId == null || p.isBundle) return false;
    const ql = q.trim().toLowerCase();
    return ql.length < 2 || p.title.toLowerCase().includes(ql);
  });
  return (
    <Modal open={open} onClose={onClose} title={mode === "sub" ? "Add a product" : "Add to your next order"}>
      <div className="relative mb-3">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" />
      </div>
      {failed ? (
        <p className="text-xs text-gray-500">Couldn't load the product list here. Ask us in the chat and we'll add it for you.</p>
      ) : !items ? (
        <div className="flex items-center gap-2 text-gray-400 text-xs py-4"><Loader2 size={13} className="animate-spin" /> Loading products…</div>
      ) : list.length === 0 ? (
        <p className="text-xs text-gray-500 py-2">No products found.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto overscroll-contain pr-0.5">
          {list.map((p) => {
            const price = mode === "sub" ? p.subPrice : p.onePrice;
            const was = mode === "sub" && p.onePrice != null && p.subPrice != null && p.onePrice > p.subPrice ? p.onePrice : null;
            return (
              <div key={p.key} className="border border-gray-200 rounded-xl overflow-hidden bg-white flex flex-col">
                {p.image
                  ? <img src={`${p.image}${p.image.includes("?") ? "&" : "?"}width=300`} alt="" loading="lazy" className="w-full aspect-square object-cover bg-gray-50" />
                  : <div className="w-full aspect-square bg-gray-50" />}
                <div className="p-2 flex flex-col gap-1 flex-1">
                  <p className="text-[11px] font-medium text-gray-900 leading-tight line-clamp-2 min-h-[2.4em]">{p.title}</p>
                  <p className="text-[11px] font-semibold text-gray-900">
                    {price != null ? money(price, "ZAR").replace(/^ZAR /, "") : ""}
                    {was != null && <span className="ml-1 text-[10px] font-normal text-gray-400 line-through">{money(was, "ZAR").replace(/^ZAR /, "")}</span>}
                  </p>
                  <button
                    type="button"
                    disabled={busyHandle === p.key}
                    onClick={() => onPick(p)}
                    className="mt-auto bg-gray-900 text-white rounded-lg py-1.5 text-[11px] font-semibold hover:bg-black disabled:opacity-50"
                  >
                    {busyHandle === p.key ? "Adding…" : "Add"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

// ── Address form (shared) ────────────────────────────────────────────────────

const ADDRESS_FIELDS: Array<{ key: string; label: string; half?: boolean }> = [
  { key: "first_name", label: "First name", half: true },
  { key: "last_name", label: "Last name", half: true },
  { key: "address1", label: "Address line 1" },
  { key: "address2", label: "Address line 2" },
  { key: "city", label: "City", half: true },
  { key: "province", label: "Province / State", half: true },
  { key: "zip", label: "Postal code", half: true },
  { key: "phone", label: "Phone", half: true },
];

const AddressForm: React.FC<{
  initial: Record<string, string>; onSave: (a: Record<string, string>) => void; busy: boolean; extraFields?: Array<{ key: string; label: string; half?: boolean }>;
}> = ({ initial, onSave, busy, extraFields }) => {
  const [form, setForm] = useState<Record<string, string>>(initial);
  const fields = [...ADDRESS_FIELDS, ...(extraFields || [])];
  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        {fields.map((f) => (
          <label key={f.key} className={`${f.half ? "" : "col-span-2"} block`}>
            <span className="block text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">{f.label}</span>
            <Input value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
          </label>
        ))}
      </div>
      <div className="mt-3">
        <ActionBtn variant="solid" busy={busy} onClick={() => onSave(form)}>Save address</ActionBtn>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FlowIQ detail
// ─────────────────────────────────────────────────────────────────────────────

const FlowiqDetail: React.FC<{
  sub: MSub; payload: any;
  call: (path: string, body?: unknown, method?: string) => Promise<any>;
  run: (key: string, fn: () => Promise<any>, notice?: string) => Promise<boolean>;
  busyKey: string; onOpenChat?: () => void;
  subscriptionWeeks?: number[] | null;
}> = ({ sub, payload, call, run, busyKey, onOpenChat, subscriptionWeeks }) => {
  const raw = sub.raw; const d = raw.data || {};
  const variantInfo: Record<string, any> = payload?.variant_info || {};
  const paymentMethod = payload?.payment_method || null;
  const cardsCount = Number(payload?.cards_count) || 0;
  const orders: any[] = Array.isArray(raw.orders) ? raw.orders : [];
  const upcoming = orders
    .filter((o) => ["request", "queued", "skipped", "escalated"].includes(String(o.status)))
    .sort((a, b) => (Number(a.order_number) || 0) - (Number(b.order_number) || 0))
    .slice(0, 4);
  const nextEntry = upcoming.find((o) => o.status === "request" || o.status === "queued") || null;
  const escCard = orders.filter((o) => o.status === "escalated" && o.payment_issue === "card");
  const escHold = orders.filter((o) => o.status === "escalated" && o.payment_issue === "provider");

  const act = (name: string) => `/subscriptions/${sub.id}/${name}`;

  // products edit state (local lineup, save posts the full list — the portal's
  // exact semantics, so migration gifts/vouchers stay preserved server-side)
  const [lineup, setLineup] = useState<Array<{ variantId: string; qty: number; title: string; image: string | null; price: number | null }>>(
    sub.lines.map((l) => ({ variantId: l.variantId!, qty: l.qty, title: l.title, image: l.image, price: l.price })),
  );
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setLineup(sub.lines.map((l) => ({ variantId: l.variantId!, qty: l.qty, title: l.title, image: l.image, price: l.price })));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub.raw]);

  // frequency
  const iv = Number(d.interval_value), iu = String(d.interval_unit || "");
  const curToken = iv && iu === "months" ? `${iv}m` : iv && iu === "weeks" ? `${iv}w` : "";
  const [freqToken, setFreqToken] = useState<string>("");
  // A cart-line store offers its own rhythms in weeks (Lean Living 2 / 4 / 6);
  // otherwise monthly / every 2 months (the "Deliver every" variant stores).
  const cartLine = Array.isArray(subscriptionWeeks) && subscriptionWeeks.length > 0;
  const freqOptions: Array<{ token: string; label: string }> = cartLine
    ? subscriptionWeeks!.map((w) => ({ token: `${w}w`, label: w === 1 ? "Every week" : `Every ${w} weeks` }))
    : [{ token: "1m", label: "Monthly" }, { token: "2m", label: "Every 2 months" }];
  const freqKnown = freqOptions.some((o) => o.token === curToken);

  // modals
  const [pauseOpen, setPauseOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeDay, setResumeDay] = useState<string>("1");
  const [cardOpen, setCardOpen] = useState(false);
  const [cards, setCards] = useState<any[] | null>(null);
  const [pickerMode, setPickerMode] = useState<null | "sub" | "onetime">(null);
  const [pickBusy, setPickBusy] = useState("");
  const [reschedFor, setReschedFor] = useState<string>(""); // entry id being rescheduled
  const [reschedDate, setReschedDate] = useState("");
  const [addrOpen, setAddrOpen] = useState(false);

  const pauseResumeAt = (months: number): string | null => {
    if (!months) return null;
    const now = new Date();
    const t = new Date(now.getFullYear(), now.getMonth() + months, 1);
    const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
    t.setDate(Math.min(now.getDate(), last));
    return toDateInput(t);
  };
  const anchorForDay = (day: string): string => {
    const now = new Date();
    const build = (y: number, m: number) => {
      const last = new Date(y, m + 1, 0).getDate();
      return new Date(y, m, day === "last" ? last : Math.min(parseInt(day, 10) || 1, last));
    };
    let y = now.getFullYear(), m = now.getMonth();
    let cand = build(y, m);
    if (cand <= new Date(y, m, now.getDate())) { m += 1; if (m > 11) { m = 0; y += 1; } cand = build(y, m); }
    return toDateInput(cand);
  };
  const currentDeliveryDay = (): number | null => {
    const o = orders.filter((x) => !["completed", "cancelled", "skipped"].includes(x.status) && x.scheduled_for)
      .sort((a, b) => String(a.scheduled_for).localeCompare(String(b.scheduled_for)))[0];
    return o ? new Date(o.scheduled_for).getDate() : null;
  };

  const openCards = async () => {
    setCardOpen(true); setCards(null);
    try { const r = await call("/cards"); setCards(r.cards || []); } catch { setCards([]); }
  };
  const addNewCard = async () => {
    const w = window.open("about:blank", "_blank");
    try {
      const r = await call("/card-update-link");
      if (w) w.location = r.url; else window.location.href = r.url;
    } catch (e) {
      if (w) { try { w.close(); } catch { /* noop */ } }
      throw e;
    }
  };

  return (
    <>
      {/* hero */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-3.5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-gray-900">{sub.title}</span>
          <StatusPill status={sub.status} />
        </div>
        <div className="text-[11px] text-gray-500 mb-3">
          {sub.freqLabel}{sub.total != null && <> · {money(sub.total, sub.currency)} {sub.discountPercent ? "next delivery" : "per delivery"}</>}
          {!!sub.discountPercent && sub.fullTotal != null && <> ({pctLabel(sub.discountPercent)} subscriber discount off {money(sub.fullTotal, sub.currency)})</>}
          {sub.status === "active" && sub.nextDate && <> · next charge {fmtDate(sub.nextDate)} ({daysUntil(sub.nextDate)})</>}
          {sub.status === "paused" && d.resume_at && <> · auto-resumes {fmtDate(d.resume_at)}</>}
        </div>
        <div className="flex flex-wrap gap-2">
          {sub.status === "active" && (
            <>
              <ActionBtn onClick={() => setPauseOpen(true)}><Pause size={12} /> Pause</ActionBtn>
              {nextEntry?.queue_entry_id && (
                <ActionBtn busy={busyKey === "skip"} onClick={() => run("skip", () => call(act("skip-order"), { queue_entry_id: nextEntry.queue_entry_id }), "Next order skipped.")}>
                  Skip next order
                </ActionBtn>
              )}
              <ActionBtn variant="ghost" onClick={() => setCancelOpen(true)}>Cancel subscription</ActionBtn>
            </>
          )}
          {sub.status === "paused" && (
            <>
              <ActionBtn variant="solid" onClick={() => {
                const cd = currentDeliveryDay();
                setResumeDay(cd && cd <= 28 ? String(cd) : cd ? "last" : "1");
                setResumeOpen(true);
              }}><Play size={12} /> Resume</ActionBtn>
              <ActionBtn variant="ghost" onClick={() => setCancelOpen(true)}>Cancel subscription</ActionBtn>
            </>
          )}
          {sub.status === "cancelled" && (
            <ActionBtn variant="solid" busy={busyKey === "reactivate"} onClick={() => run("reactivate", () => call(act("reactivate")), "Subscription reactivated.")}>
              <Play size={12} /> Reactivate
            </ActionBtn>
          )}
        </div>
      </div>

      {/* payment issue banner */}
      {sub.status !== "cancelled" && escCard.length > 0 && (
        <div className="bg-red-50 border border-red-100 rounded-2xl px-4 py-3">
          <p className="text-xs font-semibold text-red-700 mb-1">We couldn't process your payment</p>
          <p className="text-[11px] text-red-600 mb-2.5">
            Your {fmtDate(escCard[0].scheduled_for)} order didn't go through.
            {paymentMethod ? " Retry with your saved card, or switch to a different one first." : " Add or update your card, then retry."}
          </p>
          <div className="flex flex-wrap gap-2">
            <ActionBtn variant="solid" onClick={openCards}>Fix payment method</ActionBtn>
            {paymentMethod && escCard[0].queue_entry_id && (
              <ActionBtn busy={busyKey === "retry"} onClick={() => run("retry", () => call(act("retry-order"), { queue_entry_id: escCard[0].queue_entry_id }), "We'll retry your payment within a few minutes.")}>
                Retry payment now
              </ActionBtn>
            )}
          </div>
        </div>
      )}
      {sub.status !== "cancelled" && escHold.length > 0 && escCard.length === 0 && (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3">
          <p className="text-[11px] text-amber-700">
            Your {fmtDate(escHold[0].scheduled_for)} order is on hold while we fix a payment-system issue on our side — no action needed from you.
          </p>
        </div>
      )}

      {/* products */}
      <Section
        label="Products"
        right={<button type="button" onClick={() => setPickerMode("sub")} className="text-[11px] font-semibold text-gray-700 hover:text-black inline-flex items-center gap-1"><Plus size={12} /> Add</button>}
      >
        {lineup.map((l, i) => (
          <LineRow
            key={`${l.variantId}-${i}`}
            currency={sub.currency}
            line={{ key: l.variantId, lineId: null, variantId: l.variantId, title: l.title, variantTitle: null, qty: l.qty, price: l.price, image: l.image, oneTime: false }}
            onQty={sub.status === "active" ? (delta) => {
              setLineup((prev) => prev.map((x, xi) => xi === i ? { ...x, qty: Math.max(1, x.qty + delta) } : x));
              setDirty(true);
            } : undefined}
            onRemove={sub.status === "active" && lineup.length > 1 ? () => {
              setLineup((prev) => prev.filter((_, xi) => xi !== i));
              setDirty(true);
            } : undefined}
          />
        ))}
        {sub.status === "active" && (
          <div className="pt-2.5 flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Frequency</span>
            <select
              value={freqToken || (freqKnown ? curToken : "")}
              onChange={(e) => { setFreqToken(e.target.value); setDirty(true); }}
              className="border border-gray-300 rounded-xl px-2.5 py-1.5 text-xs bg-white"
            >
              {!freqKnown && <option value="">Keep current ({sub.freqLabel})</option>}
              {freqOptions.map((o) => <option key={o.token} value={o.token}>{o.label}</option>)}
            </select>
          </div>
        )}
        {dirty && (
          <div className="pt-3 flex gap-2 items-center">
            <ActionBtn variant="solid" busy={busyKey === "saveProducts"} onClick={() => run("saveProducts", async () => {
              await call(act("update-variants"), { variants: lineup.map((l) => ({ variant_id: l.variantId, quantity: l.qty })) });
              const chosen = freqToken || "";
              if (chosen && chosen !== curToken) {
                await call(act("change-frequency"), { interval_value: parseInt(chosen, 10), interval_unit: chosen.endsWith("m") ? "months" : "weeks" });
              }
            }, "Subscription updated — your recurring total has been recalculated.")}>
              Save changes
            </ActionBtn>
            <ActionBtn variant="ghost" onClick={() => {
              setLineup(sub.lines.map((l) => ({ variantId: l.variantId!, qty: l.qty, title: l.title, image: l.image, price: l.price })));
              setFreqToken(""); setDirty(false);
            }}>Discard</ActionBtn>
          </div>
        )}
      </Section>

      {/* upcoming orders */}
      {upcoming.length > 0 && (
        <Section label="Upcoming deliveries">
          {upcoming.map((o) => {
            const st = String(o.status);
            const skipped = st === "skipped";
            const esc = st === "escalated";
            return (
              <div key={o.queue_entry_id} className="py-2.5 border-b border-gray-100 last:border-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className={`text-xs font-medium ${skipped ? "line-through text-gray-400" : "text-gray-900"}`}>
                      #{o.order_number} · {fmtDate(o.scheduled_for)}
                    </div>
                    <div className={`text-[10px] ${esc ? (o.payment_issue === "provider" ? "text-amber-600 font-semibold" : "text-red-600 font-semibold") : "text-gray-400"}`}>
                      {skipped ? "Skipped" : esc ? (o.payment_issue === "provider" ? "On hold" : "Payment issue") : daysUntil(o.scheduled_for)}
                    </div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {!esc && !skipped && sub.status === "active" && (
                      <>
                        <ActionBtn className="!px-2.5 !py-1.5" onClick={() => { setReschedFor(reschedFor === o.queue_entry_id ? "" : o.queue_entry_id); setReschedDate(""); }}>Move</ActionBtn>
                        <ActionBtn className="!px-2.5 !py-1.5" busy={busyKey === `skip-${o.queue_entry_id}`} onClick={() => run(`skip-${o.queue_entry_id}`, () => call(act("skip-order"), { queue_entry_id: o.queue_entry_id }), "Order skipped.")}>Skip</ActionBtn>
                      </>
                    )}
                    {skipped && (
                      <ActionBtn className="!px-2.5 !py-1.5" busy={busyKey === `unskip-${o.queue_entry_id}`} onClick={() => run(`unskip-${o.queue_entry_id}`, () => call(act("unskip-order"), { queue_entry_id: o.queue_entry_id }), "Order reinstated.")}>Reinstate</ActionBtn>
                    )}
                    {!esc && !skipped && sub.status === "active" && (
                      <ActionBtn className="!px-2.5 !py-1.5" onClick={() => setPickerMode("onetime")}><Plus size={11} /></ActionBtn>
                    )}
                  </div>
                </div>
                {reschedFor === o.queue_entry_id && (
                  <div className="mt-2 flex gap-2 items-center">
                    <Input type="date" min={toDateInput(new Date())} value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} className="max-w-[160px]" />
                    <ActionBtn variant="solid" busy={busyKey === "resched"} onClick={() => {
                      if (!reschedDate) return;
                      run("resched", () => call(act("reschedule-order"), { queue_entry_id: o.queue_entry_id, new_date: reschedDate }), "Delivery moved.").then((ok) => { if (ok) setReschedFor(""); });
                    }}>Move date</ActionBtn>
                  </div>
                )}
                {(o.addons || []).length > 0 && (
                  <div className="mt-1.5 text-[10px] text-blue-600">
                    + {(o.addons || []).map((a: any) => a.title || "extra item").join(", ")} <span className="text-gray-400">(this order only)</span>
                  </div>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {/* payment method */}
      <Section label="Payment method">
        <div className="flex items-center justify-between gap-3 py-1">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0"><CreditCard size={15} className="text-white" /></div>
            <div className="min-w-0">
              {paymentMethod ? (
                <>
                  <p className="text-xs font-medium text-gray-900">{paymentMethod.brand || "Card"} •••• {paymentMethod.last4}</p>
                  {paymentMethod.exp_month && (
                    <p className="text-[10px] text-gray-400">Expires {`0${paymentMethod.exp_month}`.slice(-2)}/{String(paymentMethod.exp_year).slice(-2)}</p>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-500">No card on file</p>
              )}
            </div>
          </div>
          <ActionBtn onClick={openCards}>{paymentMethod ? (cardsCount > 1 ? "Change" : "Update") : "Add a card"}</ActionBtn>
        </div>
      </Section>

      {/* address */}
      <Section
        label="Delivery address"
        right={<button type="button" onClick={() => setAddrOpen(!addrOpen)} className="text-[11px] font-semibold text-gray-700 hover:text-black">{addrOpen ? "Close" : "Edit"}</button>}
      >
        {!addrOpen ? (
          <div className="flex items-start gap-2 py-1">
            <MapPin size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-gray-600 leading-relaxed">
              {(() => {
                const a = d.default_address || {};
                const parts = [[a.first_name, a.last_name].filter(Boolean).join(" "), a.address1, a.address2, [a.city, a.province, a.zip].filter(Boolean).join(", ")].filter(Boolean);
                return parts.length ? parts.join(" · ") : "No address on file yet.";
              })()}
            </p>
          </div>
        ) : (
          <AddressForm
            initial={Object.fromEntries(ADDRESS_FIELDS.map((f) => [f.key, String((d.default_address || {})[f.key] || "")]))}
            busy={busyKey === "addr"}
            onSave={(a) => run("addr", () => call(act("update-address"), { address: a }), "Delivery address updated for all future orders.").then((ok) => { if (ok) setAddrOpen(false); })}
          />
        )}
      </Section>

      {onOpenChat && (
        <p className="px-1 text-[10px] text-gray-400">
          Anything unusual? <button type="button" className="underline underline-offset-2 hover:text-gray-600" onClick={onOpenChat}>Ask us in the chat</button> — we can do it for you.
        </p>
      )}

      {/* ── modals ── */}
      <Modal open={pauseOpen} onClose={() => setPauseOpen(false)} title="Pause subscription">
        <p className="text-xs text-gray-500 mb-3">We'll hold your upcoming orders and resume automatically. You can resume early any time.</p>
        <div className="space-y-2">
          {[{ m: 1, l: "Pause for 1 month" }, { m: 2, l: "Pause for 2 months" }, { m: 0, l: "Pause indefinitely" }].map(({ m, l }) => (
            <ActionBtn key={m} className="w-full" busy={busyKey === `pause${m}`} onClick={() =>
              run(`pause${m}`, () => call(act("pause"), pauseResumeAt(m) ? { resume_at: pauseResumeAt(m) } : {}), "Subscription paused.").then((ok) => { if (ok) setPauseOpen(false); })
            }>{l}</ActionBtn>
          ))}
        </div>
      </Modal>

      <Modal open={resumeOpen} onClose={() => setResumeOpen(false)} title="Resume subscription">
        <p className="text-xs text-gray-500 mb-3">Pick which day of the month you'd like your deliveries — we'll restart on that day.</p>
        <select value={resumeDay} onChange={(e) => setResumeDay(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white mb-3">
          {Array.from({ length: 28 }, (_, i) => i + 1).map((dd) => (
            <option key={dd} value={String(dd)}>{dd}{["th", "st", "nd", "rd"][((dd % 100) - 20) % 10] || ["th", "st", "nd", "rd"][dd % 100] || "th"} of the month</option>
          ))}
          <option value="last">Last day of the month</option>
        </select>
        <ActionBtn variant="solid" className="w-full" busy={busyKey === "resume"} onClick={() =>
          run("resume", () => call(act("resume"), { anchor_date: anchorForDay(resumeDay) }), "Subscription resumed.").then((ok) => { if (ok) setResumeOpen(false); })
        }><Play size={12} /> Resume subscription</ActionBtn>
      </Modal>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel subscription?">
        <p className="text-xs text-gray-500 mb-3">Future orders will stop. You can reactivate any time.</p>
        <textarea
          value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2}
          placeholder="Reason (optional)"
          className="w-full border border-gray-300 rounded-xl px-3 py-2 text-base sm:text-sm mb-3 focus:outline-none focus:border-gray-500"
        />
        <div className="flex gap-2 justify-end">
          <ActionBtn variant="ghost" onClick={() => setCancelOpen(false)}>Keep subscription</ActionBtn>
          <ActionBtn variant="danger" busy={busyKey === "cancel"} onClick={() =>
            run("cancel", () => call(act("cancel"), cancelReason.trim() ? { reason: cancelReason.trim() } : {}), "Subscription cancelled.").then((ok) => { if (ok) setCancelOpen(false); })
          }>Cancel subscription</ActionBtn>
        </div>
      </Modal>

      <Modal open={cardOpen} onClose={() => setCardOpen(false)} title="Payment method">
        {cards === null ? (
          <div className="flex items-center gap-2 text-gray-400 text-xs py-3"><Loader2 size={13} className="animate-spin" /> Loading your cards…</div>
        ) : (
          <>
            {cards.length === 0 && <p className="text-xs text-gray-500 mb-2">No saved card yet — add one below.</p>}
            {cards.map((c) => (
              <div key={c.consent_id} className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900">{c.brand || "Card"} •••• {c.last4}</p>
                  <p className="text-[10px] text-gray-400">
                    {c.exp_month ? `Expires ${`0${c.exp_month}`.slice(-2)}/${String(c.exp_year).slice(-2)}` : ""}{c.expired ? " — expired" : ""}
                  </p>
                </div>
                {c.is_active ? (
                  <span className="text-[9px] font-semibold uppercase tracking-wide text-green-700 bg-green-50 px-2 py-1 rounded-full">Current</span>
                ) : c.expired ? (
                  <span className="text-[10px] text-red-500">Expired</span>
                ) : (
                  <ActionBtn className="!px-2.5 !py-1.5" busy={busyKey === `card-${c.consent_id}`} onClick={() =>
                    run(`card-${c.consent_id}`, () => call("/select-card", { consent_id: c.consent_id }), "Done — future charges use that card.").then((ok) => { if (ok) setCardOpen(false); })
                  }>Use this card</ActionBtn>
                )}
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-gray-100">
              <ActionBtn variant="solid" busy={busyKey === "newcard"} onClick={() => run("newcard", addNewCard, "Secure card page opened in a new tab. Once saved, your new card takes over automatically.")}>
                <Plus size={12} /> Add a new card
              </ActionBtn>
              <p className="text-[10px] text-gray-400 mt-2">You'll save the card on our secure payment page — the new card automatically becomes the one we charge.</p>
            </div>
          </>
        )}
      </Modal>

      <CataloguePicker
        open={pickerMode !== null}
        onClose={() => setPickerMode(null)}
        mode={pickerMode === "onetime" ? "onetime" : "sub"}
        busyHandle={pickBusy}
        cartLine={cartLine}
        onPick={async (p) => {
          setPickBusy(p.key);
          try {
            if (pickerMode === "sub") {
              // Append locally; Save posts the full lineup (server reprices,
              // migration perks preserved — the portal's exact semantics).
              if (!p.subVariantId) throw new Error("That product isn't available on subscription.");
              setLineup((prev) => {
                const hit = prev.findIndex((x) => x.variantId === String(p.subVariantId));
                if (hit >= 0) return prev.map((x, i) => i === hit ? { ...x, qty: x.qty + 1 } : x);
                return [...prev, { variantId: String(p.subVariantId), qty: 1, title: p.title, image: p.image || null, price: p.subPrice }];
              });
              setDirty(true);
              setPickerMode(null);
            } else {
              if (!nextEntry?.queue_entry_id) throw new Error("No upcoming order to add to.");
              if (!p.oneVariantId) throw new Error("That product isn't available as a one-off.");
              const ok = await run("addonce", () => call(act("add-item"), { queue_entry_id: nextEntry.queue_entry_id, variant_id: String(p.oneVariantId), quantity: 1 }), "Added to your next delivery only.");
              if (ok) setPickerMode(null);
            }
          } catch (e: any) {
            setPickerMode(null);
            throw e;
          } finally {
            setPickBusy("");
          }
        }}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Loop detail
// ─────────────────────────────────────────────────────────────────────────────

const LoopDetail: React.FC<{
  sub: MSub;
  call: (path: string, body?: unknown, method?: string) => Promise<any>;
  run: (key: string, fn: () => Promise<any>, notice?: string) => Promise<boolean>;
  busyKey: string; onOpenChat?: () => void;
}> = ({ sub, call, run, busyKey, onOpenChat }) => {
  const raw = sub.raw;
  const act = (name: string) => `/loop/subscriptions/${sub.id}/${name}`;

  const [pauseOpen, setPauseOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [removeLine, setRemoveLine] = useState<MLine | null>(null);
  const [pickerMode, setPickerMode] = useState<null | "sub" | "onetime">(null);
  const [pickBusy, setPickBusy] = useState("");
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedDate, setReschedDate] = useState("");
  const [addrOpen, setAddrOpen] = useState(false);
  const [freqs, setFreqs] = useState<any[] | null>(null);
  const [freqChoice, setFreqChoice] = useState("");
  const [discountCode, setDiscountCode] = useState("");

  useEffect(() => {
    if (sub.status !== "active") return;
    call(act("frequencies")).then((r) => setFreqs(r.options || [])).catch(() => setFreqs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub.id]);

  const pauseResumeDate = (months: number): string | null => {
    if (!months) return null;
    const now = new Date();
    const t = new Date(now.getFullYear(), now.getMonth() + months, 1);
    const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
    t.setDate(Math.min(now.getDate(), last));
    return toDateInput(t);
  };

  const addr = raw.shipping_address || {};
  const recurring = sub.lines.filter((l) => !l.oneTime);

  return (
    <>
      {/* hero */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-3.5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-gray-900">{sub.title}</span>
          <StatusPill status={sub.status} />
        </div>
        <div className="text-[11px] text-gray-500 mb-3">
          {sub.freqLabel}{sub.total != null && <> · {money(sub.total, sub.currency)} {sub.discountPercent ? "next delivery" : "per delivery"}</>}
          {!!sub.discountPercent && sub.fullTotal != null && <> ({pctLabel(sub.discountPercent)} subscriber discount off {money(sub.fullTotal, sub.currency)})</>}
          {sub.status === "active" && sub.nextDate && <> · next billing {fmtDate(sub.nextDate)} ({daysUntil(sub.nextDate)})</>}
        </div>
        <div className="flex flex-wrap gap-2">
          {sub.status === "active" && (
            <>
              <ActionBtn onClick={() => setPauseOpen(true)}><Pause size={12} /> Pause</ActionBtn>
              <ActionBtn busy={busyKey === "skipnext"} onClick={() => run("skipnext", () => call(act("skip-next")), "Your next order has been skipped.")}>Skip next order</ActionBtn>
              <ActionBtn onClick={() => { setReschedDate(""); setReschedOpen(true); }}><Calendar size={12} /> Move date</ActionBtn>
              <ActionBtn variant="ghost" onClick={() => setCancelOpen(true)}>Cancel subscription</ActionBtn>
            </>
          )}
          {sub.status === "paused" && (
            <>
              <ActionBtn variant="solid" busy={busyKey === "resume"} onClick={() => run("resume", () => call(act("resume")), "Subscription resumed.")}><Play size={12} /> Resume</ActionBtn>
              <ActionBtn variant="ghost" onClick={() => setCancelOpen(true)}>Cancel subscription</ActionBtn>
            </>
          )}
          {sub.status === "cancelled" && (
            <ActionBtn variant="solid" busy={busyKey === "reactivate"} onClick={() => run("reactivate", () => call(act("reactivate")), "Subscription reactivated on its original schedule.")}>
              <Play size={12} /> Reactivate
            </ActionBtn>
          )}
          {sub.status === "expired" && (
            <p className="text-[11px] text-gray-400">This subscription has ended.</p>
          )}
        </div>
      </div>

      {/* products */}
      <Section
        label="Products"
        right={sub.status === "active" ? <button type="button" onClick={() => setPickerMode("sub")} className="text-[11px] font-semibold text-gray-700 hover:text-black inline-flex items-center gap-1"><Plus size={12} /> Add</button> : undefined}
      >
        {sub.lines.map((l) => (
          <LineRow
            key={l.key}
            line={l} currency={sub.currency}
            busy={busyKey === `qty-${l.lineId}`}
            onQty={sub.status === "active" && !l.oneTime && l.lineId ? (delta) => {
              const q = Math.max(1, l.qty + delta);
              if (q === l.qty) return;
              run(`qty-${l.lineId}`, () => call(act("update-line-quantity"), { line_id: l.lineId, quantity: q }), "Quantity updated.");
            } : undefined}
            onRemove={sub.status === "active" && l.lineId && (!l.oneTime ? recurring.length > 1 : true) ? () => setRemoveLine(l) : undefined}
          />
        ))}
        {sub.status === "active" && (
          <div className="pt-2">
            <button type="button" onClick={() => setPickerMode("onetime")} className="text-[11px] text-gray-500 hover:text-gray-800 underline underline-offset-2 inline-flex items-center gap-1">
              <Plus size={11} /> Add something to the next order only
            </button>
          </div>
        )}
      </Section>

      {/* frequency */}
      {sub.status === "active" && (
        <Section label="Delivery frequency">
          {freqs === null ? (
            <div className="flex items-center gap-2 text-gray-400 text-xs py-1"><Loader2 size={12} className="animate-spin" /> Loading options…</div>
          ) : freqs.length === 0 ? (
            <p className="text-[11px] text-gray-500 py-1">Current: {sub.freqLabel}. No other options for this plan.</p>
          ) : (
            <div className="flex items-center gap-2 py-1 flex-wrap">
              <select value={freqChoice} onChange={(e) => setFreqChoice(e.target.value)} className="border border-gray-300 rounded-xl px-2.5 py-1.5 text-xs bg-white">
                <option value="">Keep current ({sub.freqLabel})</option>
                {freqs.map((f, i) => (
                  <option key={i} value={`${f.interval}|${f.interval_count}`}>{f.label}</option>
                ))}
              </select>
              {freqChoice && (
                <ActionBtn variant="solid" className="!px-3 !py-1.5" busy={busyKey === "freq"} onClick={() => {
                  const [interval, count] = freqChoice.split("|");
                  run("freq", () => call(act("change-frequency"), { interval, interval_count: Number(count) }), "Delivery frequency updated.").then((ok) => { if (ok) setFreqChoice(""); });
                }}>Apply</ActionBtn>
              )}
            </div>
          )}
        </Section>
      )}

      {/* discounts */}
      {sub.status === "active" && (
        <Section label="Discounts">
          {(raw.discounts || []).map((disc: any, i: number) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
              <span className="text-xs text-gray-700 inline-flex items-center gap-1.5"><Tag size={11} className="text-gray-400" /> {disc.title || disc.discount_shopify_id}</span>
              {disc.discount_shopify_id && (
                <button type="button" disabled={busyKey === `rmdisc-${i}`} className="text-[10px] text-gray-400 hover:text-red-600 underline underline-offset-2"
                  onClick={() => run(`rmdisc-${i}`, () => call(act("remove-discount"), { discount_shopify_id: disc.discount_shopify_id }), "Discount removed.")}>
                  Remove
                </button>
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <Input placeholder="Discount code" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} className="max-w-[180px]" />
            <ActionBtn variant="solid" busy={busyKey === "disc"} disabled={!discountCode.trim()} onClick={() =>
              run("disc", () => call(act("apply-discount"), { discount_code: discountCode.trim() }), "Discount applied.").then((ok) => { if (ok) setDiscountCode(""); })
            }>Apply</ActionBtn>
          </div>
        </Section>
      )}

      {/* payment */}
      <Section label="Payment method">
        <div className="flex items-center justify-between gap-3 py-1">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0"><CreditCard size={15} className="text-white" /></div>
            <div className="min-w-0">
              {raw.payment_method?.card_last4 ? (
                <>
                  <p className="text-xs font-medium text-gray-900">{raw.payment_method.card_brand || "Card"} •••• {raw.payment_method.card_last4}</p>
                  {raw.payment_method.card_expiry && <p className="text-[10px] text-gray-400">Expires {raw.payment_method.card_expiry}</p>}
                </>
              ) : (
                <p className="text-xs text-gray-500">Card on file</p>
              )}
            </div>
          </div>
          <ActionBtn busy={busyKey === "paylink"} onClick={() => run("paylink", () => call(act("send-payment-update-link")), "We've emailed you a secure link to update your card.")}>
            Email me an update link
          </ActionBtn>
        </div>
      </Section>

      {/* address */}
      <Section
        label="Delivery address"
        right={sub.status !== "expired" ? <button type="button" onClick={() => setAddrOpen(!addrOpen)} className="text-[11px] font-semibold text-gray-700 hover:text-black">{addrOpen ? "Close" : "Edit"}</button> : undefined}
      >
        {!addrOpen ? (
          <div className="flex items-start gap-2 py-1">
            <MapPin size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-gray-600 leading-relaxed">
              {[addr.name, addr.address1, addr.address2, [addr.city, addr.zip].filter(Boolean).join(", "), addr.country_code].filter(Boolean).join(" · ") || "No address on file."}
            </p>
          </div>
        ) : (
          <AddressForm
            initial={{
              first_name: String(addr.name || "").split(" ")[0] || "",
              last_name: String(addr.name || "").split(" ").slice(1).join(" ") || "",
              address1: addr.address1 || "", address2: addr.address2 || "",
              city: addr.city || "", province: addr.province_code || "",
              zip: addr.zip || "", phone: addr.phone || "",
              country_code: addr.country_code || "",
            }}
            extraFields={[{ key: "country_code", label: "Country code (e.g. PT)", half: true }]}
            busy={busyKey === "addr"}
            onSave={(a) => run("addr", () => call(act("update-address"), {
              first_name: a.first_name, last_name: a.last_name, phone: a.phone,
              address1: a.address1, address2: a.address2, city: a.city,
              province_code: a.province, zip: a.zip, country_code: a.country_code,
            }), "Delivery address updated.").then((ok) => { if (ok) setAddrOpen(false); })}
          />
        )}
      </Section>

      {onOpenChat && (
        <p className="px-1 text-[10px] text-gray-400">
          Anything unusual? <button type="button" className="underline underline-offset-2 hover:text-gray-600" onClick={onOpenChat}>Ask us in the chat</button> — we can do it for you.
        </p>
      )}

      {/* ── modals ── */}
      <Modal open={pauseOpen} onClose={() => setPauseOpen(false)} title="Pause subscription">
        <p className="text-xs text-gray-500 mb-3">We'll hold your orders and resume automatically. You can resume early any time.</p>
        <div className="space-y-2">
          {[{ m: 1, l: "Pause for 1 month" }, { m: 2, l: "Pause for 2 months" }, { m: 0, l: "Pause indefinitely" }].map(({ m, l }) => (
            <ActionBtn key={m} className="w-full" busy={busyKey === `pause${m}`} onClick={() => {
              const rd = pauseResumeDate(m);
              run(`pause${m}`, () => call(act("pause"), rd ? { resume_date: rd } : {}), "Subscription paused.").then((ok) => { if (ok) setPauseOpen(false); });
            }}>{l}</ActionBtn>
          ))}
        </div>
      </Modal>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel subscription?">
        <p className="text-xs text-gray-500 mb-3">Future orders will stop. You can reactivate any time.</p>
        <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} rows={2} placeholder="Reason (optional)"
          className="w-full border border-gray-300 rounded-xl px-3 py-2 text-base sm:text-sm mb-3 focus:outline-none focus:border-gray-500" />
        <div className="flex gap-2 justify-end">
          <ActionBtn variant="ghost" onClick={() => setCancelOpen(false)}>Keep subscription</ActionBtn>
          <ActionBtn variant="danger" busy={busyKey === "cancel"} onClick={() =>
            run("cancel", () => call(act("cancel"), cancelReason.trim() ? { reason: cancelReason.trim() } : {}), "Subscription cancelled.").then((ok) => { if (ok) setCancelOpen(false); })
          }>Cancel subscription</ActionBtn>
        </div>
      </Modal>

      <Modal open={reschedOpen} onClose={() => setReschedOpen(false)} title="Move your next delivery">
        <p className="text-xs text-gray-500 mb-3">Pick the new billing date — future orders shift with it.</p>
        <Input type="date" min={toDateInput(new Date(Date.now() + 86400000))} value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} className="mb-3" />
        <ActionBtn variant="solid" className="w-full" busy={busyKey === "resched"} disabled={!reschedDate} onClick={() =>
          run("resched", () => call(act("reschedule"), { new_date: reschedDate }), "Next billing date moved.").then((ok) => { if (ok) setReschedOpen(false); })
        }>Move date</ActionBtn>
      </Modal>

      <Modal open={removeLine !== null} onClose={() => setRemoveLine(null)} title={`Remove ${removeLine?.title || "product"}?`}>
        {removeLine?.oneTime ? (
          <ActionBtn variant="danger" className="w-full" busy={busyKey === "rmline"} onClick={() =>
            run("rmline", () => call(act("remove-line-once"), { line_id: removeLine!.lineId }), "Removed from your next order.").then((ok) => { if (ok) setRemoveLine(null); })
          }>Remove from next order</ActionBtn>
        ) : (
          <div className="space-y-2">
            <ActionBtn className="w-full" busy={busyKey === "rmonce"} onClick={() =>
              run("rmonce", () => call(act("remove-line-once"), { line_id: removeLine!.lineId }), "Skipped on your next order — it stays on the subscription.").then((ok) => { if (ok) setRemoveLine(null); })
            }>Just the next order</ActionBtn>
            <ActionBtn variant="danger" className="w-full" busy={busyKey === "rmline"} onClick={() =>
              run("rmline", () => call(act("remove-line"), { line_id: removeLine!.lineId }), "Removed from all future orders.").then((ok) => { if (ok) setRemoveLine(null); })
            }>Remove permanently</ActionBtn>
          </div>
        )}
      </Modal>

      <CataloguePicker
        open={pickerMode !== null}
        onClose={() => setPickerMode(null)}
        mode={pickerMode === "onetime" ? "onetime" : "sub"}
        busyHandle={pickBusy}
        onPick={async (p) => {
          setPickBusy(p.key);
          try {
            const variantId = pickerMode === "sub" ? (p.subVariantId ?? p.oneVariantId) : (p.oneVariantId ?? p.subVariantId);
            if (!variantId) throw new Error("That product has no available variant.");
            const ok = await run(
              "addline",
              () => call(act(pickerMode === "sub" ? "add-line" : "add-line-once"), { variant_shopify_id: variantId, quantity: 1 }),
              pickerMode === "sub" ? "Added — it'll come with every delivery." : "Added to your next order only.",
            );
            if (ok) setPickerMode(null);
          } finally {
            setPickBusy("");
          }
        }}
      />
    </>
  );
};

export default ManageSubscriptionsView;
